/**
 * Deterministic "random" choices that stay stable for the local calendar day,
 * then change at midnight. Uses a seeded PRNG (Mulberry32) + date string.
 */

/** Local calendar date as YYYY-MM-DD */
export function getLocalDateKey() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** FNV-1a string hash — deterministic, stable across runs (used to seed the PRNG below). */
export function hashStringToSeed(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(seed) {
  return function next() {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Fisher–Yates shuffle with order fixed per day + salt (e.g. "portfolio").
 */
export function seededShuffleForDay(items, salt = "") {
  if (!Array.isArray(items) || items.length < 2) {
    return Array.isArray(items) ? [...items] : [];
  }
  const seed = hashStringToSeed(`${getLocalDateKey()}|${salt}`);
  const rng = mulberry32(seed);
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/**
 * Pick one index in [0, length) — same result all day for the same salt (e.g. collection key).
 */
export function pickDailyIndex(length, salt = "") {
  if (length <= 0) return 0;
  const seed = hashStringToSeed(`${getLocalDateKey()}|${salt}`);
  const rng = mulberry32(seed);
  return Math.floor(rng() * length);
}
